export const DAILY_REPORT_PDF_TABLE_VALUES = [
  "timesheets",
  "work_items",
  "equipment_timesheets",
  "quantity_logs",
  "material_logs",
  "notes",
] as const;
export type DailyReportPdfTable = (typeof DAILY_REPORT_PDF_TABLE_VALUES)[number];

export type DailyReportPdfColumnDef = {
  /** Must match the `field` used when building PDF columns in daily-report-service.ts */
  key: string;
  label: string;
  defaultVisible: boolean;
};

/**
 * Static, ordered catalog of the columns each daily report PDF table can show.
 * Timesheets lists only the CORE columns here; the policy-derived timesheet
 * columns (TIMESHEET_EXTENDED_PDF_FIELD_KEYS + timesheet custom fields) are
 * appended dynamically so the dimension/custom-field gating logic isn't
 * duplicated between the dashboard and backend.
 *
 * The `missed_lunch` and `signature` timesheet columns are intentionally
 * absent: they remain governed by the existing `show_missed_lunch` and
 * `show_timesheet_signature_in_pdf` company settings.
 */
export const DAILY_REPORT_PDF_FIELDS: Record<DailyReportPdfTable, DailyReportPdfColumnDef[]> = {
  timesheets: [
    { key: "group_label", label: "Team member", defaultVisible: true },
    { key: "activity", label: "Activity", defaultVisible: true },
    { key: "clock_in", label: "Clock in", defaultVisible: true },
    { key: "clock_out", label: "Clock out", defaultVisible: true },
    { key: "injury", label: "Injured", defaultVisible: true },
    { key: "hours", label: "Hours", defaultVisible: true },
  ],
  work_items: [
    { key: "name", label: "Name", defaultVisible: true },
    { key: "description", label: "Description", defaultVisible: true },
  ],
  equipment_timesheets: [
    { key: "equipment", label: "Equipment", defaultVisible: true },
    { key: "code", label: "Equipment code", defaultVisible: true },
    { key: "job", label: "Job", defaultVisible: true },
    { key: "cost_code", label: "Cost code", defaultVisible: true },
    { key: "hours", label: "Hours", defaultVisible: true },
    { key: "team_member", label: "Team member", defaultVisible: true },
  ],
  quantity_logs: [
    { key: "activity", label: "Activity", defaultVisible: true },
    { key: "quantity", label: "Qty", defaultVisible: true },
    { key: "notes", label: "Note", defaultVisible: true },
    { key: "team_member", label: "Team member", defaultVisible: true },
  ],
  // Daily reports are often emailed to customers, so the cost columns are hidden on the PDF unless a company turns them on.
  material_logs: [
    { key: "material", label: "Material", defaultVisible: true },
    { key: "type", label: "Type", defaultVisible: true },
    { key: "quantity", label: "Qty", defaultVisible: true },
    { key: "activity", label: "Activity", defaultVisible: true },
    { key: "vendor", label: "Vendor", defaultVisible: true },
    { key: "reference_number", label: "Ref No", defaultVisible: true },
    { key: "unit_cost", label: "Unit cost", defaultVisible: false },
    { key: "total_cost", label: "Total cost", defaultVisible: false },
    { key: "notes", label: "Note", defaultVisible: true },
  ],
  notes: [
    { key: "type", label: "Type", defaultVisible: true },
    { key: "content", label: "Content", defaultVisible: true },
  ],
};

/**
 * Ordered, policy-derived timesheet columns that can be enabled on the PDF.
 * All default OFF. Keys match the field values used by timesheet policies
 * (see useTimesheetPolicyFieldOptions) so the settings editor and the PDF
 * generator agree on identifiers. Label resolution and dimension gating live
 * in the dashboard hook; the backend has a matching value-getter map.
 */
export const TIMESHEET_EXTENDED_PDF_FIELDS: DailyReportPdfColumnDef[] = [
  { key: "job", label: "Job", defaultVisible: false },
  { key: "notes", label: "Notes", defaultVisible: false },
  { key: "department_id", label: "Department", defaultVisible: false },
  { key: "class_id", label: "Class", defaultVisible: false },
  { key: "location_id", label: "Location", defaultVisible: false },
  { key: "equipment_ids", label: "Equipment", defaultVisible: false },
  { key: "cost_type_id", label: "Cost type", defaultVisible: false },
  { key: "work_order_id", label: "Work order", defaultVisible: false },
  { key: "rate_differential_id", label: "Rate differential", defaultVisible: false },
  { key: "classification_override", label: "Classification", defaultVisible: false },
  { key: "earning_type", label: "Earning type", defaultVisible: false },
  { key: "images", label: "Photos", defaultVisible: false },
  { key: "clock_in_photo", label: "Clock in photo", defaultVisible: false },
  { key: "clock_out_photo", label: "Clock out photo", defaultVisible: false },
];

export const TIMESHEET_EXTENDED_PDF_FIELD_KEYS = TIMESHEET_EXTENDED_PDF_FIELDS.map((f) => f.key);

/**
 * Per-table visibility overrides for the daily report PDF. Only explicit
 * deviations from the catalog defaults are stored (visible = override ??
 * default), so an absent/empty config always yields today's exact PDF output.
 * Keys are column field keys; for the timesheets table they may also be
 * timesheet CustomField ids, and `custom_fields` keys are daily_report
 * CustomField ids.
 */
export type DailyReportPdfFieldVisibility = Partial<Record<DailyReportPdfTable, Record<string, boolean>>> & {
  custom_fields?: Record<string, boolean>;
};
